import { GEOMETRY } from "./canvas-geometry";
import type {
  ComponentDefinition,
  ComponentNode,
  Terminal,
  TerminalSide,
} from "./types";

/** Rotate a (side, offset) pair clockwise. Offsets increase left→right on
 *  top/bottom and top→bottom on left/right, so rotation reverses some legs —
 *  hence the (1 − offset) on those. */
function rotateSideOffset(
  side: TerminalSide,
  offset: number,
  rotation: 0 | 90 | 180 | 270,
): { side: TerminalSide; offset: number } {
  if (rotation === 0) return { side, offset };
  if (rotation === 90) {
    if (side === "top") return { side: "right", offset };
    if (side === "right") return { side: "bottom", offset: 1 - offset };
    if (side === "bottom") return { side: "left", offset };
    return { side: "top", offset: 1 - offset };
  }
  if (rotation === 180) {
    if (side === "top") return { side: "bottom", offset: 1 - offset };
    if (side === "right") return { side: "left", offset: 1 - offset };
    if (side === "bottom") return { side: "top", offset: 1 - offset };
    return { side: "right", offset: 1 - offset };
  }
  // 270° CW (= 90° CCW)
  if (side === "top") return { side: "left", offset: 1 - offset };
  if (side === "right") return { side: "top", offset };
  if (side === "bottom") return { side: "right", offset: 1 - offset };
  return { side: "bottom", offset };
}

/** Apply per-instance terminal overrides to the catalog terminal list. Busbar
 *  connections can be pinned to either long face without changing stud ids. */
export function effectiveTerminals(
  def: ComponentDefinition,
  node: Pick<ComponentNode, "terminalOverrides" | "terminalLabels" | "rotation" | "studCount" | "isFusebox">,
): Terminal[] {
  const withLabel = (t: Terminal): Terminal => {
    const label = node.terminalLabels?.[t.id];
    return label === undefined || label === t.label ? t : { ...t, label };
  };
  if (def.renderVariant === "busbar") {
    const rotation = node.rotation ?? 0;
    const overrides = node.terminalOverrides;
    const studs = busbarStudList(def, node.studCount);
    // Excludes *every* catalog stud, not just the ids the synthesized list
    // reuses: otherwise shrinking `studCount` leaks the spares back in and the
    // swap planner puts cables on studs the user cannot see.
    const nonStuds = def.terminals.filter((t) => !isStudTerminal(def, t));
    const all = [...nonStuds, ...studs];
    const intended = new Map<string, number>();
    const sides = new Map<string, TerminalSide>();
    const vertical = def.height >= def.width;
    for (const t of all) {
      const ov = overrides?.[t.id];
      const offset =
        ov?.pinned && typeof ov.offset === "number" ? ov.offset : t.offset;
      intended.set(t.id, offset);
      const requestedFace = ov?.busbarFace;
      const side = ov?.pinned && requestedFace && (vertical
        ? requestedFace === "left" || requestedFace === "right"
        : requestedFace === "top" || requestedFace === "bottom") ? requestedFace : t.side;
      sides.set(t.id, side);
    }
    // Two pins saved at one offset overlap the inline fuse pills; the
    // conflicting override falls back to its canonical offset.
    const dims = effectiveDimensions(def, { ...node, rotation: 0 });
    const sideLengthPx = vertical ? dims.height : dims.width;
    for (const t of all) {
      const ov = overrides?.[t.id];
      if (!ov?.pinned || typeof ov.offset !== "number") continue;
      // Existing drawings used the catalog length for collisions and never
      // adjusted the feed. Keep their rendered layout until explicitly moved.
      if (!ov.busbarFace && !isStudTerminal(def, t)) continue;
      const minGap = GEOMETRY.MIN_TERMINAL_GAP_PX / Math.max(1, ov.busbarFace
        ? sideLengthPx : vertical ? def.height : def.width);
      const myOffset = intended.get(t.id)!;
      const collides = all.some(
        (other) =>
          other.id !== t.id &&
          (sides.get(other.id) === sides.get(t.id) ||
            !!node.isFusebox && isStudTerminal(def, t) && isStudTerminal(def, other)) &&
          Math.abs(intended.get(other.id)! - myOffset) < minGap,
      );
      if (collides) intended.set(t.id, t.offset);
    }
    return all.map(withLabel).map((t) => {
      const offset = intended.get(t.id)!;
      const side = sides.get(t.id)!;
      if (rotation === 0) return offset === t.offset && side === t.side ? t : { ...t, side, offset };
      const r = rotateSideOffset(side, offset, rotation);
      return { ...t, side: r.side, offset: r.offset };
    });
  }
  const rotation = node.rotation ?? 0;
  const isSymbolVariant =
    def.renderVariant === "switch" ||
    def.renderVariant === "fuse-holder" ||
    def.renderVariant === "chassis-ground";
  // Symbol variants bake terminal positions into the glyph, so an override would
  // float the handle dot off the lead end. Ignoring them here has to match
  // `optimalTerminalOverrides` and the drag-blocker in component-node.tsx.
  const overrides = isSymbolVariant ? undefined : node.terminalOverrides;
  const labeled = node.terminalLabels ? def.terminals.map(withLabel) : def.terminals;
  const base = overrides
    ? labeled.map((t) => {
        const o = overrides[t.id];
        if (!o) return t;
        return {
          ...t,
          side: o.side ?? t.side,
          offset: o.offset ?? t.offset,
        };
      })
    : labeled;
  // A terminal added to the catalog after the user pinned its siblings carries
  // no override and can land on top of one.
  const resolved =
    overrides && base.some((t) => !overrides[t.id])
      ? resolveTerminalCollisions(def, base, overrides)
      : base;
  if (!isSymbolVariant || rotation === 0) return resolved;
  return resolved.map((t) => {
    const r = rotateSideOffset(t.side, t.offset, rotation);
    return { ...t, side: r.side, offset: r.offset };
  });
}

function resolveTerminalCollisions(
  def: ComponentDefinition,
  base: Terminal[],
  overrides: NonNullable<ComponentNode["terminalOverrides"]>,
): Terminal[] {
  const sideLengthPx = (side: TerminalSide) =>
    side === "left" || side === "right" ? def.height : def.width;
  return base.map((t) => {
    if (overrides[t.id]) return t; // user-placed → never move
    const siblings = base.filter((o) => o.id !== t.id && o.side === t.side);
    const minGap = GEOMETRY.MIN_TERMINAL_GAP_PX / Math.max(1, sideLengthPx(t.side));
    if (!siblings.some((o) => Math.abs(o.offset - t.offset) < minGap)) return t;
    const occupied = siblings.map((o) => o.offset).sort((a, b) => a - b);
    const bounds = [0, ...occupied, 1];
    let bestOffset = t.offset;
    let bestGap = -1;
    for (let i = 0; i < bounds.length - 1; i++) {
      const gap = bounds[i + 1] - bounds[i];
      if (gap > bestGap) {
        bestGap = gap;
        bestOffset = (bounds[i] + bounds[i + 1]) / 2;
      }
    }
    return { ...t, offset: bestOffset };
  });
}

/** Bounding box for a component instance. Terminal overrides deliberately never
 *  change it — the component would resize under the user mid-drag. */
export function effectiveDimensions(
  def: ComponentDefinition,
  node: Pick<ComponentNode, "terminalOverrides" | "rotation" | "studCount" | "isFusebox">,
): { width: number; height: number } {
  const r = node.rotation ?? 0;
  let width = def.width;
  let height = def.height;
  if (def.renderVariant === "busbar") {
    const catalogStuds = catalogStudCount(def);
    const studCount = Math.max(1, node.studCount ?? catalogStuds);
    if (catalogStuds > 0 && studCount !== catalogStuds) {
      const ratio = studCount / catalogStuds;
      if (def.height >= def.width) height = def.height * ratio;
      else width = def.width * ratio;
    }
    // Fusebox mode thickens the non-stud axis to fit the inline fuse pills.
    if (node.isFusebox) {
      const FUSEBOX_THICKNESS = 90;
      if (def.height >= def.width) {
        width = Math.max(width, FUSEBOX_THICKNESS);
      } else {
        height = Math.max(height, FUSEBOX_THICKNESS);
      }
    }
  }
  // Card variants carry a photo lockup; without a floor it collapses to a sliver.
  const variant = def.renderVariant ?? "card";
  if (variant === "card") {
    width = Math.max(width, 150);
    height = Math.max(height, 110);
  }
  if (r === 90 || r === 270) {
    return { width: height, height: width };
  }
  return { width, height };
}

/** Physical stud identity comes from the catalog, not the connection's drawn
 *  face. Terminals synthesized beyond the catalog count are also studs. */
export function isStudTerminal(def: ComponentDefinition, t: Terminal): boolean {
  const studAxis: "left" | "right" | "top" | "bottom" =
    def.height >= def.width ? "right" : "bottom";
  const catalogTerminal = def.terminals.find(original => original.id === t.id);
  if (def.renderVariant === "busbar" && !catalogTerminal) return true;
  return (catalogTerminal ?? t).side === studAxis;
}

function catalogStudCount(def: ComponentDefinition): number {
  return def.terminals.filter((t) => isStudTerminal(def, t)).length;
}

/** The effective stud list for a busbar. An unset `studCount` returns the catalog
 *  terminals verbatim; otherwise studs are evenly spaced, reusing catalog ids
 *  first so cable refs stay valid, then synthesized `stud-N` ids. */
export function busbarStudList(
  def: ComponentDefinition,
  studCount: number | undefined,
): Terminal[] {
  if (def.renderVariant !== "busbar") return [];
  const catalogStuds = def.terminals
    .filter((t) => isStudTerminal(def, t))
    .slice();
  if (studCount == null) return catalogStuds;
  const target = Math.max(1, studCount);
  const studAxis: "left" | "right" | "top" | "bottom" =
    def.height >= def.width ? "right" : "bottom";
  const result: Terminal[] = [];
  for (let i = 0; i < target; i++) {
    const offset = (i + 0.5) / target;
    if (i < catalogStuds.length) {
      const base = catalogStuds[i];
      result.push({ ...base, side: studAxis, offset });
    } else {
      const ref = catalogStuds[0];
      const maxAmp = ref?.maxAmp ?? 150;
      const polarity = ref?.polarity ?? "positive";
      const direction = ref?.direction ?? "bidi";
      result.push({
        // Earlier 2303 drawings exposed two extra catalog taps. Keep saved
        // custom stud counts connected after restoring its four physical posts.
        id: def.id === "dist-bs-powerbar-2114" && i < 5
          ? ["b", "c", "d", "e", "f"][i]
          : `stud-${i + 1}`,
        label: String(i + 1),
        polarity,
        direction,
        side: studAxis,
        offset,
        maxAmp,
      });
    }
  }
  return result;
}
